import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { theme, typography } from "../Homepage/styles";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const LoginPage = styled.div`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: ${theme.bg};
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
`;

/** Lovable: absolute inset-x-0 top-0 h-[420px] bg-gradient-hero */
export const LoginGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0 0 auto;
  height: 420px;
  background-image: ${theme.gradientHero};
`;

export const LoginShell = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 64px; /* clear fixed Header */
`;

export const LoginMain = styled.main`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`;

export const LoginInner = styled.div`
  width: 100%;
  max-width: 28rem; /* max-w-md */
`;

/** Lovable glass-card: bg-card/80 backdrop-blur-xl border border-border/50 */
export const LoginCard = styled.div`
  padding: 20px;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  border-radius: 1rem; /* rounded-2xl */
  background: hsl(var(--hp-card) / 0.8);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: ${theme.shadowCard};

  @media (min-width: 640px) {
    padding: 40px;
  }
`;

export const LoginCardHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;
`;

export const LoginTitle = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.875rem; /* text-3xl */
  font-weight: ${typography.weights.bold};
  letter-spacing: ${typography.tracking.tight};
  line-height: 1.2;
`;

export const LoginSubtitle = styled.p`
  margin: 8px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.9375rem;
  line-height: 1.5;
`;

export const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px; /* space-y-5 */
`;

/** Lovable Alert destructive */
export const LoginAlert = styled.div`
  position: relative;
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 16px 16px 16px 44px;
  border: 1px solid hsl(0 72% 48% / 0.5);
  border-radius: 0.5rem;
  color: hsl(0 72% 48%);
  font-size: 0.875rem;
  line-height: 1.5;
  animation: ${fadeIn} 0.6s ease-out forwards;

  html[data-theme="dark"] & {
    border-color: hsl(0 84.2% 60.2%);
    color: hsl(0 84.2% 60.2%);
  }

  svg {
    position: absolute;
    top: 16px;
    left: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

/** Lovable Alert default (success status) */
export const LoginSuccessAlert = styled.div`
  position: relative;
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 16px 16px 16px 44px;
  border: 1px solid hsl(var(--hp-border));
  border-radius: 0.5rem;
  background: hsl(var(--hp-background));
  color: ${theme.foreground};
  font-size: 0.875rem;
  line-height: 1.5;
  animation: ${fadeIn} 0.6s ease-out forwards;

  svg {
    position: absolute;
    top: 16px;
    left: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: hsl(160 84% 39%);
  }
`;

export const LoginField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LoginLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const LoginLabel = styled.label`
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1;
`;

export const LoginForgotLink = styled(Link)`
  color: ${theme.primary} !important;
  font-size: 0.875rem;
  text-decoration: none;
  text-underline-offset: 4px;

  &:hover,
  &:focus,
  &:active {
    color: ${theme.primary} !important;
    text-decoration: underline;
    text-decoration-color: ${theme.primary};
  }
`;

export const LoginInputWrap = styled.div`
  position: relative;
`;

export const LoginInputIcon = styled.span`
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 1;
  display: flex;
  color: ${theme.mutedForeground};
  transform: translateY(-50%);

  svg {
    width: 16px;
    height: 16px;
  }
`;

/** Lovable Input + Login overrides: pl-9 h-11 bg-background/50 */
export const LoginInput = styled.input`
  display: flex;
  width: 100%;
  height: 2.75rem; /* h-11 */
  padding: 8px 12px 8px 36px; /* pl-9 */
  border: 1px solid hsl(var(--hp-border));
  border-radius: 0.375rem; /* rounded-md */
  background: hsl(var(--hp-background) / 0.5);
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  line-height: 1.4;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${theme.mutedForeground};
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${theme.bg},
      0 0 0 4px hsl(var(--hp-ring));
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/**
 * Lovable Button hero + lg:
 * h-12 rounded-lg px-8 text-base font-bold bg-gradient-gold
 * shadow + hover scale
 */
export const LoginSubmit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 3rem; /* h-12 */
  padding: 0 2rem;
  border: none;
  border-radius: 0.5rem; /* rounded-lg */
  background-image: ${theme.gradientGold};
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.4);
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 1rem;
  font-weight: ${typography.weights.bold};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    transform: scale(1.03);
    box-shadow: 0 6px 30px hsl(32 95% 55% / 0.5);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${theme.bg},
      0 0 0 4px hsl(var(--hp-ring)),
      0 4px 20px hsl(32 95% 55% / 0.4);
  }
`;

export const LoginSpinIcon = styled.span`
  display: inline-flex;
  animation: login-spin 0.8s linear infinite;

  @keyframes login-spin {
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const LoginFooterNote = styled.p`
  margin: 24px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  text-align: center;
`;

export const LoginFooterLink = styled(Link)`
  color: ${theme.primary} !important;
  font-weight: ${typography.weights.semibold};
  text-decoration: none;
  text-underline-offset: 4px;

  &:hover,
  &:focus,
  &:active {
    color: ${theme.primary} !important;
    text-decoration: underline;
    text-decoration-color: ${theme.primary};
  }
`;

export const LoginHomeWrap = styled.div`
  margin-top: 32px;
  text-align: center;
`;

export const LoginHomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.foreground};
  }
`;
